import { Box, Stack } from '@atgfr8/atg-ui-tailwind';
import { AlertCircleIcon, AlertIcon, InformationIcon } from '@atgfr8/icons';
import { For, Match, Show, Switch } from 'solid-js';

import classes from './classes';
import type { AlertSeverity, SystemAlert } from './types';

type AlertCardProps = {
  severity: AlertSeverity;
  alerts: SystemAlert[];
};

const SEVERITY_CONFIG = {
  notice: {
    card: classes.noticeCard,
    title: classes.noticeCardTitle,
    singular: 'Notice',
    plural: 'Notices',
  },
  block: {
    card: classes.blockCard,
    title: classes.blockCardTitle,
    singular: 'Blocker',
    plural: 'Blockers',
  },
  warning: {
    card: classes.warningCard,
    title: classes.warningCardTitle,
    singular: 'Warning',
    plural: 'Warnings',
  },
} as const;

const SeverityIcon = (props: { severity: AlertSeverity }) => (
  <Switch>
    <Match when={props.severity === 'notice'}>
      <InformationIcon style={{ color: '#026ea1' }} />
    </Match>
    <Match when={props.severity === 'block'}>
      <AlertCircleIcon style={{ color: '#b00020' }} />
    </Match>
    <Match when={props.severity === 'warning'}>
      <AlertIcon color="#ff8f00" />
    </Match>
  </Switch>
);

const AlertRow = (props: { alert: SystemAlert; showDivider: boolean }) => {
  return (
    <Box
      class={props.showDivider ? classes.rowDivider : ''}
      dataTestId={`system-alert-${props.alert.key}`}
    >
      <Show
        when={props.alert.render}
        fallback={
          <Stack direction="row" class={classes.row}>
            <Stack direction="column">
              <Show when={props.alert.title}>
                <div class={classes.rowTitle}>{props.alert.title}</div>
              </Show>
              <div class={classes.rowText}>{props.alert.message}</div>
            </Stack>
            <Show when={props.alert.action}>{props.alert.action!()}</Show>
          </Stack>
        }
      >
        <Stack direction="row" class={classes.row}>
          {props.alert.render!()}
        </Stack>
      </Show>
    </Box>
  );
};

export const AlertCard = (props: AlertCardProps) => {
  const config = () => SEVERITY_CONFIG[props.severity];
  const count = () => props.alerts.length;
  const label = () => (count() === 1 ? config().singular : config().plural);

  return (
    <Show when={count() > 0}>
      <Stack direction="column" class={config().card}>
        <Stack direction="row" class={config().title}>
          <SeverityIcon severity={props.severity} />
          <span>
            {count()} {label()}
          </span>
        </Stack>
        <Box class={classes.rowsWrap}>
          <For each={props.alerts}>
            {(alert, index) => (
              <AlertRow alert={alert} showDivider={index() > 0} />
            )}
          </For>
        </Box>
      </Stack>
    </Show>
  );
};
